import { useAudioSrcRefresh } from "../hooks/useAudioSrcRefresh.ts";
import { useAutoSave } from "../hooks/useAutoSave.ts";
import { useMemo, useRef } from "react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import styled from "styled-components";

import { Button } from "../components/Button.tsx";
import { Stack } from "../components/Stack.tsx";
import { SectionHeader } from "../components/SectionHeader.tsx";
import { SegmentRow } from "../components/SegmentRow.tsx";
import { Slider } from "../components/Slider.tsx";
import { SaveIndicator } from "../components/SaveIndicator.tsx";
import { useSamplePlayer } from "../hooks/useSamplePlayer.ts";
import { useNavigateTo, useSyncParams, useRequireMeetingData } from "../routing.ts";

import {
  audioFilenameAtom,
  customSpeakersAtom,
  manualOverridesAtom,
  profilesAtom,
  thresholdAtom,
  currentProcessingIdAtom,
} from "../../state/atoms.ts";
import {
  audioUrlAtom,
  finalSegmentsAtom,
  speakerListAtom,
} from "../../state/derived.ts";

import type { TokenSegment } from "../../lib/types.ts";

const PADDING_BEFORE = 2;
const PADDING_AFTER = 2;

export function ReviewScreen() {
  // Renueva la PAR de audio.wav automáticamente si el audio viene del bucket.
  useAudioSrcRefresh();

  // Fase 14 — autoguardado debounced + on-blur.
  const { resolveConflict } = useAutoSave();

  const audioUrl = useAtomValue(audioUrlAtom);
  const audioFilename = useAtomValue(audioFilenameAtom);
  const isMp4 = audioFilename === "audio.mp4";
  const videoUrl = isMp4 ? audioUrl : null;
  const allSegments = useAtomValue(finalSegmentsAtom);
  const speakers = useAtomValue(speakerListAtom);
  const profiles = useAtomValue(profilesAtom);

  const processingId = useAtomValue(currentProcessingIdAtom);
  const [overrides, setOverrides] = useAtom(manualOverridesAtom);
  const [customSpeakers, setCustomSpeakers] = useAtom(customSpeakersAtom);
  const setProfiles = useSetAtom(profilesAtom);
  const navigateTo = useNavigateTo();
  useSyncParams();
  useRequireMeetingData();

  // Fase 14 — threshold desde atom (0..1) en lugar de local state (0-100).
  // El Slider trabaja en escala 0-100; la conversión ocurre en value/onChange.
  const [threshold, setThreshold] = useAtom(thresholdAtom);
  const thresholdPct = Math.round(threshold * 100);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [{ playingId, progress }, { play }] = useSamplePlayer(audioRef);

  // --- Segmentos a revisar: avgConfidence < threshold ---
  const lowConfSegments = useMemo(
    () => allSegments.filter((s) => s.avgConfidence < threshold),
    [allSegments, threshold],
  );

  const totalToReview = lowConfSegments.length;
  const hasNothing = totalToReview === 0;

  // --- Handlers ---

  const handlePlaySegment = (segIndex: number, start: number, dur: number) => {
    void play(`seg-${segIndex}`, start, {
      duration: dur,
      paddingBefore: PADDING_BEFORE,
      paddingAfter: PADDING_AFTER,
    });
  };

  const handleChangeSpeaker = (
    segIndex: number,
    originalSpeaker: number,
    newSpeaker: number,
  ) => {
    const next = new Map(overrides);
    if (newSpeaker === originalSpeaker) {
      next.delete(segIndex);
    } else {
      next.set(segIndex, newSpeaker);
    }
    setOverrides(next);
  };

  const approveGroup = (segments: TokenSegment[]) => {
    const next = new Map(overrides);
    for (const seg of segments) {
      if (!next.has(seg.index)) {
        // speakerIndex ya refleja el override si había uno (finalSegmentsAtom).
        next.set(seg.index, seg.speakerIndex);
      }
    }
    setOverrides(next);
  };

  const clearGroup = (segments: TokenSegment[]) => {
    const next = new Map(overrides);
    for (const seg of segments) next.delete(seg.index);
    setOverrides(next);
  };

  const handleAddManualSpeaker = () => {
    const min = customSpeakers.length === 0 ? 0 : Math.min(...customSpeakers);
    const newIndex = min - 1;
    setCustomSpeakers([...customSpeakers, newIndex]);
    setProfiles(
      new Map(profiles).set(newIndex, { index: newIndex, name: "", role: "" }),
    );
    alert(
      "Speaker agregado. Para que aparezca en el SRT final, volvé a Identify y completá nombre y cargo.",
    );
  };

  const renderRow = (seg: TokenSegment) => {
    const id = `seg-${seg.index}`;
    const override = overrides.get(seg.index);
    const current = override ?? seg.speakerIndex;
    return (
      <SegmentRow
        key={seg.index}
        segment={seg}
        speakers={speakers}
        profiles={profiles}
        currentSpeaker={current}
        hasOverride={override !== undefined}
        paddingBefore={PADDING_BEFORE}
        paddingAfter={PADDING_AFTER}
        isPlaying={playingId === id}
        progress={playingId === id ? progress : 0}
        videoUrl={videoUrl}
        onPlay={() =>
          handlePlaySegment(seg.index, seg.startTime, seg.endTime - seg.startTime)
        }
        onChangeSpeaker={(sp) =>
          handleChangeSpeaker(seg.index, seg.speakerIndex, sp)
        }
      />
    );
  };

  const overridesInGroup = (segs: TokenSegment[]): number =>
    segs.filter((s) => overrides.has(s.index)).length;

  const goBack = () => processingId && navigateTo.identify(processingId);
  const goNext = () => processingId && navigateTo.export(processingId);

  // --- Vista vacía ---
  if (hasNothing) {
    return (
      <Container>
        <Header>
          <Title>Revisar segmentos dudosos</Title>
          <Subtitle>
            No hay segmentos que requieran revisión con un umbral de {thresholdPct}%.
          </Subtitle>
        </Header>

        <Card>
          <Slider
            label="Umbral de confianza"
            min={0}
            max={100}
            value={thresholdPct}
            format={(v) => `${v}%`}
            onChange={(v) => setThreshold(v / 100)}
          />
          <ThresholdSummary>
            <SummaryChip $tone="muted">
              {allSegments.length} segmentos · todos con confianza ≥ {thresholdPct}%
            </SummaryChip>
          </ThresholdSummary>
        </Card>

        {/* Fase 14 — banner de conflicto o badge de estado */}
        <SaveIndicator onResolveConflict={resolveConflict} />

        <Footer>
          <Button $variant="ghost" onClick={goBack}>
            ← Volver
          </Button>
          <Button onClick={goNext}>Continuar →</Button>
        </Footer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Revisar segmentos dudosos</Title>
        <Subtitle>
          {totalToReview} {totalToReview === 1 ? "segmento" : "segmentos"} con
          confianza de transcripción baja (&lt; {thresholdPct}%).
          La reproducción incluye {PADDING_BEFORE}s antes y {PADDING_AFTER}s después.
        </Subtitle>
      </Header>

      <Card>
        <Slider
          label="Umbral de confianza (segmentos por debajo se consideran dudosos)"
          min={0}
          max={100}
          value={thresholdPct}
          format={(v) => `${v}%`}
          onChange={(v) => setThreshold(v / 100)}
        />
        <ThresholdSummary>
          <SummaryChip $tone="warning">
            {lowConfSegments.length} con baja confianza (&lt; {thresholdPct}%)
          </SummaryChip>
          <SummaryChip $tone="muted">
            {allSegments.length - lowConfSegments.length} con confianza OK
          </SummaryChip>
        </ThresholdSummary>
        <ManualSpeakerRow>
          <span>¿Falta una voz que Oracle no detectó?</span>
          <Button $variant="ghost" onClick={handleAddManualSpeaker}>
            + Agregar speaker manualmente
          </Button>
        </ManualSpeakerRow>
      </Card>

      {audioUrl && <audio ref={audioRef} src={audioUrl} preload="auto" muted={isMp4} />}

      <SectionWrapper>
        <SectionTitleRow>
          <SectionHeader
            tone="warning"
            title={`Baja confianza (< ${thresholdPct}%)`}
            count={lowConfSegments.length}
            description="Whisper tuvo baja confianza en la transcripción. Revisá el speaker asignado."
          />
          <GroupActions>
            <Button
              $variant="ghost"
              onClick={() => approveGroup(lowConfSegments)}
              disabled={overridesInGroup(lowConfSegments) === lowConfSegments.length}
            >
              Aprobar todos
            </Button>
            <Button
              $variant="ghost"
              onClick={() => clearGroup(lowConfSegments)}
              disabled={overridesInGroup(lowConfSegments) === 0}
            >
              Quitar overrides
            </Button>
          </GroupActions>
        </SectionTitleRow>
        <Stack $gap="sm">{lowConfSegments.map(renderRow)}</Stack>
      </SectionWrapper>

      {/* Fase 14 — banner de conflicto o badge de estado */}
      <SaveIndicator onResolveConflict={resolveConflict} />

      <Footer>
        <FooterLeft>
          <Button $variant="ghost" onClick={goBack}>
            ← Volver
          </Button>
        </FooterLeft>
        <FooterRight>
          <ModifiedSummary>
            {overrides.size} {overrides.size === 1 ? "modificado" : "modificados"} en total
          </ModifiedSummary>
          <Button onClick={goNext}>Continuar →</Button>
        </FooterRight>
      </Footer>
    </Container>
  );
}

// --- Estilos ---

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.xl};
`;

const Header = styled.div``;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.font.size.xxl};
  font-weight: ${({ theme }) => theme.font.weight.bold};
  margin: 0 0 ${({ theme }) => theme.space.xs} 0;
  color: ${({ theme }) => theme.color.textPrimary};
  letter-spacing: -0.02em;
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.font.size.md};
  color: ${({ theme }) => theme.color.textMuted};
  margin: 0;
`;

const Card = styled.div`
  background: ${({ theme }) => theme.color.surface};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.space.lg};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.md};
`;

const ThresholdSummary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.space.xs};
`;

const SummaryChip = styled.span<{ $tone: "warning" | "muted" }>`
  font-size: ${({ theme }) => theme.font.size.xs};
  font-family: ${({ theme }) => theme.font.mono};
  padding: 4px 10px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme, $tone }) =>
    $tone === "warning" ? theme.color.warningMuted : theme.color.background};
  color: ${({ theme, $tone }) =>
    $tone === "warning" ? theme.color.warning : theme.color.textMuted};
  border: 1px solid
    ${({ theme, $tone }) =>
      $tone === "warning" ? theme.color.warning : theme.color.border};
`;

const ManualSpeakerRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textMuted};
  padding-top: ${({ theme }) => theme.space.sm};
  border-top: 1px solid ${({ theme }) => theme.color.border};
`;

const SectionWrapper = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.sm};
`;

const SectionTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.space.md};
  flex-wrap: wrap;
`;

const GroupActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.xs};
  flex-shrink: 0;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: ${({ theme }) => theme.space.md};
  border-top: 1px solid ${({ theme }) => theme.color.border};
`;

const FooterLeft = styled.div``;

const FooterRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.md};
`;

const ModifiedSummary = styled.span`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textMuted};
  font-family: ${({ theme }) => theme.font.mono};
`;
